import { cn, Select, TextField, Typography } from "@suraasa/placebo-ui"
import clsx from "clsx"
import { Search, Sort, SortDown, SortUp } from "iconoir-react"
import get from "lodash/get"
import { Fragment, useCallback, useState } from "react"

import type { DataTableProps, FilterValues, SortConfig } from "./types"

type Option = {
  label: string
  value: string
}

export const DataTable = <T extends Record<string, any>>({
  title,
  titleIcon,
  columns,
  data,
  headerClassName,
  showSearch = false,
  searchPlaceholder = "Search...",
  filters = [],
  loading = false,
  emptyMessage = "No data available",
  className,
  containerClassName,
  onRowClick,
  actionSlot,
  onQueryChange,
}: DataTableProps<T>) => {
  const [searchTerm, setSearchTerm] = useState("")
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null)
  const [filterValues, setFilterValues] = useState<FilterValues>({})

  // Memoized callback to avoid unnecessary re-renders
  const triggerQueryChange = useCallback(
    (search: string, filters: FilterValues, sort: SortConfig | null) => {
      onQueryChange?.({
        search: search || undefined,
        filters: Object.keys(filters).length > 0 ? filters : undefined,
        sort,
      })
    },
    [onQueryChange],
  )

  const handleSearchChange = (value: string) => {
    setSearchTerm(value)
    triggerQueryChange(value, filterValues, sortConfig)
  }

  const handleFilterChange = (key: string, value: string) => {
    const newFilters = { ...filterValues, [key]: value }
    setFilterValues(newFilters)
    triggerQueryChange(searchTerm, newFilters, sortConfig)
  }

  const handleSort = (key: string) => {
    const column = columns.find((col) => col.key === key)
    if (!column?.sortable) return

    const newSort = (() => {
      if (sortConfig?.key === key) {
        if (sortConfig.direction === "asc") {
          return { key, direction: "desc" as const }
        }
        return null // Remove sorting
      }
      return { key, direction: "asc" as const }
    })()

    setSortConfig(newSort)
    triggerQueryChange(searchTerm, filterValues, newSort)
  }

  const getSortIcon = (key: string) => {
    const column = columns.find((col) => col.key === key)
    if (!column?.sortable) return null

    if (sortConfig?.key === key) {
      return sortConfig.direction === "asc" ? (
        <SortUp className="size-2 shrink-0" />
      ) : (
        <SortDown className="size-2 shrink-0" />
      )
    }
    return <Sort className="size-2 shrink-0" />
  }

  const showHeader =
    title || titleIcon || showSearch || filters.length > 0 || actionSlot

  return (
    <div className={className}>
      {showHeader && (
        <div className="flex flex-col items-start justify-between gap-3 overflow-hidden py-3 lg:flex-row lg:items-center lg:gap-0">
          {(titleIcon || title) && (
            <div className="mt-0.5 flex items-center gap-1">
              {titleIcon}
              <Typography variant="title3" className="text-onSurface-600">
                {title}
              </Typography>
            </div>
          )}

          {/* Search and Filters */}
          {(showSearch || filters.length > 0 || actionSlot) && (
            <div className="flex w-full flex-col items-start justify-between gap-2 sm:flex-row sm:items-center lg:flex-1">
              {showSearch && (
                <TextField
                  startIcon={<Search fontSize={12} />}
                  type="text"
                  placeholder={searchPlaceholder}
                  value={searchTerm}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className={clsx(
                    "min-w-[229px] [&>div]:!h-[38px] [&>div]:!rounded-xl [&>div]:!border-onSurface-300",
                    {
                      "w-full sm:w-fit sm:!min-w-[400px]": filters.length <= 1,
                    },
                    { "!min-w-[300px]": filters.length === 2 },
                  )}
                />
              )}

              <div className="flex w-full flex-row justify-end gap-2 sm:w-auto">
                {filters.length > 0 && (
                  <div className="flex w-full flex-wrap items-center gap-1.5 lg:w-fit">
                    {filters.map((filter) => (
                      <Fragment key={filter.key}>
                        {filter.render ? (
                          filter.render()
                        ) : (
                          <Select<Option>
                            placeholder={filter.label}
                            options={filter.options}
                            mountOnBody
                            value={
                              filter.options?.find(
                                (opt) => opt.value === filterValues[filter.key],
                              ) ?? null
                            }
                            onChange={(val) => {
                              if (val?.value)
                                handleFilterChange(filter.key, val.value)
                            }}
                            className="z-[11] min-w-[145px] max-w-full shrink-0 sm:w-auto [&>div>div>div>div]:!font-semibold [&>div>div>div>div]:!text-onSurface-700 [&>div>div>div>span]:!hidden [&>div>div>div]:cursor-pointer [&>div>div]:!rounded-xl [&>div>div]:!border-onSurface-300"
                          />
                        )}
                      </Fragment>
                    ))}
                  </div>
                )}
                {actionSlot && <div>{actionSlot}</div>}
              </div>
            </div>
          )}
        </div>
      )}
      {/* Table */}
      <div className="mb-3">
        {/* Table Container */}
        <div
          className={clsx(
            "overflow-auto rounded-xl border border-onSurface-200",
            containerClassName,
          )}
        >
          <table className="w-full border-collapse">
            <thead className="sticky top-0 z-10">
              <tr
                className={cn(
                  "bg-onSurface-50 text-onSurface-600",
                  headerClassName,
                )}
              >
                {columns.map((column) => (
                  <th
                    key={column.key}
                    className={clsx(
                      "select-none break-words p-2 align-top",
                      {
                        "text-center": column.align === "center",
                        "text-right": column.align === "right",
                        "text-left": !["center", "right"].includes(
                          column.align ?? "",
                        ),
                        "cursor-pointer": column.sortable,
                      },
                      column.className,
                    )}
                    style={{ width: column.width, minWidth: column.width }}
                    onClick={() => handleSort(column.key)}
                  >
                    <Typography
                      className={clsx("flex items-start gap-1", {
                        "justify-center": column.align === "center",
                        "justify-end": column.align === "right",
                        "justify-start": !["center", "right"].includes(
                          column.align ?? "",
                        ),
                      })}
                      variant="strongSmallBody"
                    >
                      {column.label}
                      {getSortIcon(column.key)}
                    </Typography>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr
                    key={`skeleton-${i}`}
                    className="border-b border-gray-100"
                  >
                    {columns.map((column, ci) => (
                      <td
                        key={column.key || ci}
                        className={clsx("p-2", {
                          "text-center": column.align === "center",
                          "text-right": column.align === "right",
                          "text-left": !["center", "right"].includes(
                            column.align ?? "",
                          ),
                        })}
                        style={{ width: column.width }}
                      >
                        <div className="h-4 w-full animate-pulse rounded bg-gray-200" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : data?.length === 0 && !loading ? (
                <tr>
                  <td
                    colSpan={columns.length}
                    className="p-4 text-center text-gray-500"
                  >
                    {emptyMessage}
                  </td>
                </tr>
              ) : (
                data?.map((row, index) => (
                  <tr
                    key={index}
                    className={clsx(
                      "border-b border-gray-100 hover:bg-gray-50",
                      {
                        "cursor-pointer": !!onRowClick,
                      },
                    )}
                    onClick={() => onRowClick?.(row)}
                  >
                    {columns.map((column) => (
                      <td
                        key={column.key}
                        className={clsx("p-2", {
                          "text-center": column.align === "center",
                          "text-right": column.align === "right",
                          "text-left": !["center", "right"].includes(
                            column.align ?? "",
                          ),
                        })}
                        style={{
                          width: column.width,
                        }}
                      >
                        {column.render
                          ? column.render(get(row, column.key), row)
                          : get(row, column.key)}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
